import { ChangeDetectionStrategy, Component, forwardRef, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { uniqueId } from 'lodash-es';

import { NotificationService } from '@blaze-shared/platform-data-access';
import { ButtonIconComponent, TooltipDirective } from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

@UntilDestroy()
@Component({
  selector: 'blaze-secret-input',
  imports: [ButtonIconComponent, IconsModule, ReactiveFormsModule, TooltipDirective],
  templateUrl: './secret-input.component.html',
  styleUrl: './secret-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SecretInputComponent),
      multi: true,
    },
  ],
})
export class SecretInputComponent implements ControlValueAccessor {
  private readonly notificationService = inject(NotificationService);

  readonly controlId = uniqueId('webhook-id-input-');

  secretCtrl = new FormControl<string>('');

  onChange?: (value: string | null) => void;
  onTouched?: () => void;

  constructor() {
    this.secretCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.onChange?.(value);
    });
  }

  writeValue(value?: string): void {
    this.secretCtrl.setValue(value ?? '');
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  generateNewSecret(): void {
    const newSecret = crypto.randomUUID();
    this.secretCtrl.setValue(newSecret);
  }

  copySecretToClipboard(): void {
    const value = this.secretCtrl.value;
    if (!value) {
      return;
    }
    navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success(`Copied to clipboard`, false, 3000);
    });
  }
}
